import React, { useState, useEffect } from 'react';
import VideoPlayer from '../../components/VideoPlayer';
import {
  getVideoOrientation,
  getVideoThumbnail,
  detectOrientationFromThumbnail
} from '../../shared/videoUtils';
import {
  LuImage,
  LuMaximize2,
  LuX,
  LuBookOpen,
  LuSchool,
  LuUsers,
  LuLoader
} from 'react-icons/lu';
import { FaFacebook } from 'react-icons/fa';
import { PiChurchLight } from 'react-icons/pi';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../shared/firebase';
import { extractString, extractOrder } from '../../shared/fieldUtils';
import galleryHero from '../../assets/Contactus.png';

function getMediaImage(d) {
  if (!d) return '';
  const candidate =
    d.thumbnail ||
    d.thumb ||
    d.img_url ||
    d.image_url ||
    d.image ||
    d.photo ||
    d.img ||
    d.pic ||
    d.cover ||
    d.poster ||
    d.imageUrl ||
    d.bgImg ||
    d.bg_img ||
    d.picture ||
    d.banner ||
    (typeof d.url === 'string' && !d.url.includes('facebook.com') && !d.url.includes('fb.watch') && !d.url.includes('youtube.com') && !d.url.includes('youtu.be') ? d.url : '');
  return extractString(candidate);
}

function getMediaLink(d) {
  if (!d) return '';
  return extractString(
    d.link ||
    d.video_url ||
    d.fb_url ||
    d.facebook_link ||
    (typeof d.url === 'string' && (d.url.includes('facebook.com') || d.url.includes('fb.watch') || d.url.includes('youtube.com') || d.url.includes('youtu.be')) ? d.url : '')
  );
}

function getTimestampMs(item) {
  if (!item) return 0;
  const t = item.updatedAt || item.createdAt || item.timestamp || item.time_stamp || item.date;
  if (!t) return 0;
  if (typeof t.toDate === 'function') return t.toDate().getTime();
  if (t.seconds) return t.seconds * 1000;
  if (typeof t === 'number') return t;
  if (typeof t === 'string') {
    const d = new Date(t);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  }
  return 0;
}

export default function PhotoGallery({ activeSection = 'gallery', onSelectSection = () => { } }) {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalOrientation, setModalOrientation] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;
    if (selectedItem) {
      const videoUrl = selectedItem.videoLink || selectedItem.link || (typeof selectedItem.url === 'string' && (selectedItem.url.includes('facebook.com') || selectedItem.url.includes('youtube.com')) ? selectedItem.url : '');
      const initial = getVideoOrientation(selectedItem);
      setModalOrientation(initial === 'portrait' ? 'portrait' : 'landscape');

      if (!videoUrl) {
        const thumbUrl = selectedItem.url || selectedItem.thumbnail;
        if (thumbUrl && typeof thumbUrl === 'string') {
          detectOrientationFromThumbnail(thumbUrl).then((detected) => {
            if (!isCancelled && detected) {
              setModalOrientation(detected === 'portrait' ? 'portrait' : 'landscape');
            }
          });
        }
      }
    } else {
      setModalOrientation(null);
    }
    return () => { isCancelled = true; };
  }, [selectedItem]);

  useEffect(() => {
    let isMounted = true;
    async function fetchGalleryData() {
      try {
        setLoading(true);
        const combined = [];
        const seenDocIds = new Set();

        // 1. Fetch Facebook Videos from all video collection names
        const videoCollections = ['fb-video', 'fb_video', 'facebook-video', 'facebook_videos', 'fbvideos', 'videos'];
        for (const colName of videoCollections) {
          const fbSnap = await getDocs(collection(db, colName)).catch(() => null);
          if (fbSnap && !fbSnap.empty) {
            fbSnap.docs.forEach(doc => {
              if (seenDocIds.has(doc.id)) return;
              seenDocIds.add(doc.id);
              const d = doc.data();
              const link = getMediaLink(d);
              const title = extractString(d.title || d.name || d.caption || d.description || d.desc, "Diocesan Facebook Video");
              const description = extractString(d.description || d.desc || d.details || d.summary || d.subtitle || d.message || d.caption || d.text || "");
              const customThumb = getMediaImage(d);
              const resolvedUrl = customThumb || getVideoThumbnail(link || d) || galleryHero;
              const date = extractString(d.date || d.created_at || d.createdAt, "Recent");
              const order = extractOrder(d.order);

              if (link || resolvedUrl || title) {
                combined.push({
                  id: doc.id,
                  ...d,
                  title,
                  description,
                  category: "Facebook Videos",
                  date,
                  type: 'video',
                  url: resolvedUrl,
                  thumbnail: customThumb || resolvedUrl,
                  videoLink: link,
                  videoWidth: d.videoWidth || d.width || d.video_width || null,
                  videoHeight: d.videoHeight || d.height || d.video_height || null,
                  orientation: d.orientation || d.videoOrientation || d.video_orientation || d.aspectRatio || d.aspect_ratio || d.format || '',
                  order
                });
              }
            });
          }
        }

        // 2. Fetch Photos from 'gallery' / 'photo-gallery' / 'photos'
        const photoCollections = ['gallery', 'photo-gallery', 'photos'];
        for (const colName of photoCollections) {
          const photoSnap = await getDocs(collection(db, colName)).catch(() => null);
          if (photoSnap && !photoSnap.empty) {
            photoSnap.docs.forEach(doc => {
              if (seenDocIds.has(doc.id)) return;
              seenDocIds.add(doc.id);
              const d = doc.data();
              const image = getMediaImage(d);
              const title = extractString(d.title || d.name || d.caption, "Diocesan Photo");
              const category = extractString(d.category, "Parishes & Sanctuaries");
              const date = extractString(d.date || d.place, "Diocese of Malabar");
              const link = getMediaLink(d);
              const order = extractOrder(d.order);

              if (image) {
                combined.push({
                  id: doc.id,
                  ...d,
                  title,
                  category,
                  date,
                  type: link ? 'video' : 'photo',
                  url: image,
                  thumbnail: image,
                  videoLink: link,
                  videoWidth: d.videoWidth || d.width || d.video_width || null,
                  videoHeight: d.videoHeight || d.height || d.video_height || null,
                  orientation: d.orientation || d.aspectRatio || d.aspect_ratio || d.format || '',
                  order
                });
              }
            });
          }
        }

        if (isMounted) {
          combined.sort((a, b) => {
            const orderA = a.order !== undefined ? a.order : 999;
            const orderB = b.order !== undefined ? b.order : 999;
            if (orderA !== orderB) return orderA - orderB;
            return getTimestampMs(b) - getTimestampMs(a);
          });
          setMediaItems(combined);
        }
      } catch (err) {
        console.error("Error fetching gallery:", err);
        if (isMounted) setMediaItems([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchGalleryData();
    return () => { isMounted = false; };
  }, []);

  // Build dynamic categories list
  const uniqueCategories = ['All', ...new Set(mediaItems.map(m => m.category).filter(Boolean))];

  const filteredItems = selectedCategory === 'All'
    ? mediaItems
    : mediaItems.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#2D2420] font-sans pb-16">

      {/* ── 1. Hero Header Banner ── */}
      <section className="relative w-full overflow-hidden min-h-[380px] sm:min-h-[420px] lg:min-h-[440px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={galleryHero}
            alt="Moments of Faith Cathedral and Community Sanctuary"
            className="w-full h-full object-cover object-[right_center] filter brightness-[0.75] contrast-[1.08]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#140e1b]/90 via-[#140e1b]/40 to-black/30"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 sm:pt-32 pb-16 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
          
          <div className="max-w-xl text-left">
            <h1 className="font-serif font-bold text-white text-[38px] sm:text-[48px] lg:text-[56px] tracking-[0.04em] uppercase leading-[1.05] drop-shadow-md">
              MOMENTS OF FAITH
            </h1>
            <p className="text-[#E8B86D] text-[15px] sm:text-[17px] font-medium tracking-wide mt-2.5 drop-shadow-sm">
              A visual chronicle and video archives celebrating our worshipping parishes, youth conventions, pastoral ordinations, and charitable missions across Malabar.
            </p>
            <div className="flex items-center gap-2 text-xs text-white/80 font-medium mt-4">
              <span onClick={() => onSelectSection('home')} className="hover:text-white cursor-pointer transition-colors">Home</span>
              <span className="text-[#E8B86D] font-bold">›</span>
              <span className="text-white font-semibold">Media &amp; Gallery</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <LuImage className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">
                  {mediaItems.length}+
                </span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Media Archives</span>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <PiChurchLight className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">84+</span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Parishes</span>
              </div>
            </div>
          </div>

        </div>

        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-10 sm:h-12 text-[#FAF7F2] fill-current">
            <path d="M0,0 C150,90 350,-40 500,60 C650,160 900,10 1200,40 L1200,120 L0,120 Z"></path>
          </svg>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-8">

        {/* Category Filter Pills */}
        <div className="bg-white rounded-[20px] shadow-[0_10px_35px_rgba(0,0,0,0.04)] p-4 sm:p-5 border border-gray-100 flex items-center gap-2 overflow-x-auto scrollbar-none">
          {uniqueCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`text-xs font-semibold px-4 py-2.5 rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${selectedCategory === cat
                ? 'bg-[#842343] text-white shadow-sm'
                : 'bg-[#FAF7F2] text-[#5A4F4A] hover:bg-gray-100 border border-gray-200/80'
                }`}
            >
              {cat === 'Facebook Videos' && <FaFacebook className="w-3.5 h-3.5 text-blue-500" />}
              <span>{cat}</span>
            </button>
          ))}
        </div>

        {/* Media Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[24px] border border-[#E8DDD7]">
            <LuLoader className="w-10 h-10 text-[#842343] animate-spin mb-3" />
            <p className="text-sm font-semibold text-[#5A4F4A]">Loading Moments of Faith...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-[24px] border border-[#E8DDD7] p-8">
            <h3 className="font-serif font-bold text-xl text-[#842343]">No Media Items Found</h3>
            <p className="text-sm text-[#7B6B66] mt-1">Please select another category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {filteredItems.map((item) => {
              const isVideo = item.type === 'video' || Boolean(item.videoLink);

              return (
                <div
                  key={item.id}
                  className="bg-white rounded-[22px] overflow-hidden border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_35px_rgba(132,35,67,0.12)] transition-all duration-300 flex flex-col hover:-translate-y-1"
                >
                  <div className="relative h-60 w-full overflow-hidden bg-black flex items-center justify-center">
                    {/* If video has a videoLink and no separate photo, render original Facebook player */}
                    {isVideo && item.videoLink ? (
                      <div className="w-full h-full relative">
                        <iframe
                          title={item.title}
                          src={
                            item.videoLink.includes('facebook.com') || item.videoLink.includes('fb.watch')
                              ? `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(item.videoLink)}&show_text=false&allowfullscreen=true&autoplay=false`
                              : item.videoLink
                          }
                          className="w-full h-full border-0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen={true}
                        ></iframe>
                      </div>
                    ) : (
                      <div
                        onClick={() => setSelectedItem(item)}
                        className="w-full h-full cursor-pointer relative group"
                      >
                        <img
                          src={item.url || ''}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = galleryHero;
                          }}
                          className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                          <div className="text-white">
                            <span className="text-[10px] uppercase tracking-wider text-[#e5c98b] font-bold block mb-1">
                              {item.category}
                            </span>
                            <p className="text-xs font-serif font-bold leading-tight">
                              {item.title}
                            </p>
                          </div>
                        </div>
                        <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 backdrop-blur-xs flex items-center justify-center text-[#842343] shadow-md opacity-0 group-hover:opacity-100 transition-opacity">
                          <LuMaximize2 className="w-4 h-4" />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-4 flex justify-between items-center">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#842343] block mb-1">
                        {item.date}
                      </span>
                      <h3 className="font-serif font-bold text-[#1a1715] text-[14px] leading-snug line-clamp-2">
                        {item.title}
                      </h3>
                    </div>

                    {item.videoLink && (
                      <a
                        href={item.videoLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-full bg-[#1877F2] text-white flex items-center justify-center shrink-0 hover:scale-110 transition-transform shadow-sm"
                        title="Open on Facebook"
                      >
                        <FaFacebook className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>

      {/* Media Lightbox / Modal with Dynamic Landscape/Portrait Sizing */}
      {selectedItem && (() => {
        const videoUrl = selectedItem.videoLink || selectedItem.link || (typeof selectedItem.url === 'string' && (selectedItem.url.includes('facebook.com') || selectedItem.url.includes('youtube.com')) ? selectedItem.url : '');
        const currentOrientation = modalOrientation || (getVideoOrientation(selectedItem) === 'portrait' ? 'portrait' : 'landscape');

        const isPortrait = currentOrientation === 'portrait';
        const modalStyle = {
          aspectRatio: isPortrait ? '9 / 16' : '16 / 9',
          maxWidth: isPortrait ? '420px' : '920px',
          width: '92%',
          maxHeight: isPortrait ? '85vh' : '82vh',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        };

        const activeItemWithOrientation = {
          ...selectedItem,
          orientation: isPortrait ? 'portrait' : 'landscape'
        };

        return (
          <div
            className="fixed inset-0 bg-black/85 backdrop-blur-md z-[100] flex items-center justify-center p-3 sm:p-6 w-screen h-screen"
            onClick={() => setSelectedItem(null)}
          >
            <div
              style={modalStyle}
              className="bg-[#1C1622] rounded-2xl overflow-hidden shadow-2xl relative border border-white/15 text-white flex flex-col my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <div className="absolute top-3 right-3 z-30 flex items-center">
                <button
                  onClick={() => setSelectedItem(null)}
                  className="w-8 h-8 rounded-full bg-black/70 hover:bg-black/90 flex items-center justify-center text-white transition-colors cursor-pointer backdrop-blur-md border border-white/20 shadow-md"
                  title="Close"
                >
                  <LuX className="w-4 h-4" />
                </button>
              </div>

              <div className="relative w-full flex-1 bg-black flex items-center justify-center overflow-hidden">
                {videoUrl ? (
                  <VideoPlayer
                    key={selectedItem.id || videoUrl}
                    item={activeItemWithOrientation}
                    url={videoUrl}
                    title={selectedItem.title}
                    autoplay={true}
                  />
                ) : (
                  <img
                    src={selectedItem.url}
                    alt={selectedItem.title}
                    className="w-full h-full object-contain"
                    onLoad={(e) => {
                      const { naturalWidth, naturalHeight } = e.currentTarget;
                      if (naturalWidth && naturalHeight) {
                        setModalOrientation(naturalHeight > naturalWidth ? 'portrait' : 'landscape');
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Bottom Statistics Strip ── */}
      <section className="mt-14 pt-8 pb-8 bg-[#f5ede5]/70 border-t border-[#ede2d6]">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 text-center">

            <div className="flex items-center justify-center gap-3">
              <PiChurchLight className="w-7 h-7 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">26+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Dioceses</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuBookOpen className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">5</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Districts</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuSchool className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">120+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Institutions</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuUsers className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">49</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Presbyters</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuUsers className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">4.5M+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Members</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <span className="text-[#842343] text-xl font-serif">✝</span>
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">100+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Worship Services</span>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
